import React, { useState, useRef, useEffect } from "react";
import { ChevronLeftIcon } from "@heroicons/react/24/solid";
import { useNavigate } from "react-router-dom";
import { chat } from "../../api/user";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

interface Message {
  id: number;
  content: string;
  sender: "user" | "bot";
}

// A custom component for expandable images.
const ExpandableImage: React.FC<{ src?: string; alt?: string }> = ({ src, alt }) => {
  const [expanded, setExpanded] = useState(false);
  return (
    <img
      src={src}
      alt={alt || "Embedded content"}
      className={`my-2 max-w-full ${expanded ? "" : "max-h-40 cursor-pointer"}`}
      onClick={() => setExpanded(!expanded)}
    />
  );
};

// Custom markdown renderer that replaces our image skeleton token.
const MarkdownWithImageSkeleton: React.FC<{ content: string }> = ({ content }) => {
  // Split the content by our placeholder token.
  const parts = content.split("%%IMAGE_SKELETON%%");
  return (
    <>
      {parts.map((part, index) => (
        <React.Fragment key={index}>
          {/* Render the text part via ReactMarkdown */}
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={{
              a: ({ node, ...props }) => {
                if (props.href && props.href.includes("scontent")) {
                  return (
                    <ExpandableImage src={props.href} alt="scontent image" />
                  );
                }
                return (
                  <a
                    {...props}
                    className="text-yellow underline"
                    target="_blank"
                    rel="noopener noreferrer"
                  />
                );
              },
              img: ({ node, ...props }) => <ExpandableImage {...props} />,
            }}
            className="text-left text-sm text-white whitespace-pre-wrap inline"
          >
            {part}
          </ReactMarkdown>
          {/* If not the last segment, insert the skeleton placeholder */}
          {index !== parts.length - 1 && (
            <div className="animate-pulse bg-gray-700 h-40 w-full my-2"></div>
          )}
        </React.Fragment>
      ))}
    </>
  );
};

const Chat = () => {
  const navigate = useNavigate();
  const [messages, setMessages] = useState<Message[]>([]);
  const [chatInput, setChatInput] = useState("");
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom whenever messages update.
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleSend = async () => {
    if (!chatInput.trim()) return;

    // Create user message object.
    const userMessage: Message = {
      id: Date.now(),
      content: chatInput.trim(),
      sender: "user",
    };

    // Update state with user's message.
    setMessages((prev) => [...prev, userMessage]);

    // Build the conversation in the required format.
    const conversation = [...messages, userMessage].map((msg) => ({
      role: msg.sender === "user" ? "user" : "assistant",
      content: msg.content,
    }));

    setChatInput("");

    try {
      // Create an empty bot message that will be streamed.
      const botMessage: Message = {
        id: Date.now() + 1,
        content: "",
        sender: "bot",
      };
      setMessages((prev) => [...prev, botMessage]);

      const response = await chat(conversation, 1);
      const fullText = response.reply || "This is a bot reply.";

      // Pre-process fullText for markdown image links like [View Image](URL)
      const imageMarkdownRegex = /\[View Image\]\((https?:\/\/\S*scontent\S*)\)/g;
      const segments: Array<{ type: "text" | "image"; content?: string; url?: string }> = [];
      let lastIndex = 0;
      let match;
      while ((match = imageMarkdownRegex.exec(fullText)) !== null) {
        if (match.index > lastIndex) {
          segments.push({
            type: "text",
            content: fullText.substring(lastIndex, match.index),
          });
        }
        // For images, store the URL.
        segments.push({
          type: "image",
          url: match[1],
        });
        lastIndex = imageMarkdownRegex.lastIndex;
      }
      if (lastIndex < fullText.length) {
        segments.push({
          type: "text",
          content: fullText.substring(lastIndex),
        });
      }
      
      // Start streaming simulated reply.
      let segmentIndex = 0;
      let charIndex = 0;
      let displayed = "";
      
      const interval = setInterval(() => {
        if (segmentIndex >= segments.length) {
          // Streaming complete, build the final content:
          clearInterval(interval);
          let finalContent = "";
          for (const segment of segments) {
            if (segment.type === "text") {
              finalContent += segment.content;
            } else if (segment.type === "image") {
              // Replace skeleton token with the actual markdown image link.
              finalContent += `[View Image](${segment.url})`;
            }
          }
          setMessages((prev) =>
            prev.map((msg) =>
              msg.id === botMessage.id ? { ...msg, content: finalContent } : msg
            )
          );
          return;
        }
        const segment = segments[segmentIndex];
        if (segment.type === "text") {
          if (segment.content) {
            charIndex++;
            if (charIndex <= segment.content.length) {
              displayed += segment.content.charAt(charIndex - 1);
              setMessages((prev) =>
                prev.map((msg) =>
                  msg.id === botMessage.id ? { ...msg, content: displayed } : msg
                )
              );
            } else {
              segmentIndex++;
              charIndex = 0;
            }
          } else {
            segmentIndex++;
          }
        } else if (segment.type === "image") {
          // While streaming, immediately add the skeleton token.
          displayed += "%%IMAGE_SKELETON%%";
          setMessages((prev) =>
            prev.map((msg) =>
              msg.id === botMessage.id ? { ...msg, content: displayed } : msg
            )
          );
          segmentIndex++;
          charIndex = 0;
        }
      }, 5);
    } catch (error) {
      console.error("Error sending chat message:", error);
    }
  };

  // Handler for textarea key press – sending on Enter without Shift.
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="h-screen bg-black flex flex-col">
      {/* Nav Bar */}
      <div className="h-20 bg-grey flex items-center border-b border-white border-opacity-20 px-4">
        <button
          onClick={() => navigate("/global/settings")}
          className="mr-4 hover:cursor-pointer"
        >
          <ChevronLeftIcon className="w-6 h-6 text-white" />
        </button>
        <h1 className="text-white text-xl font-semibold">Chat</h1>
      </div>

      {/* Chat messages container */}
      <div className="flex-grow overflow-y-auto p-4 space-y-4">
        {messages.map((msg) => (
          <div
            key={msg.id}
            className={`flex ${
              msg.sender === "bot" ? "justify-end" : "justify-start"
            }`}
          >
            <div
              className={`p-3 rounded-lg ${
                msg.sender === "bot"
                  ? "w-[500px] bg-grey"
                  : "bg-violet inline-block max-w-full"
              }`}
            >
              {/* Use our custom markdown renderer */}
              <MarkdownWithImageSkeleton content={msg.content} />
            </div>
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>

      {/* Input area */}
      <div className="p-4 border-t border-white border-opacity-20">
        <textarea
          className="bg-grey border border-white outline-none p-2 w-full text-sm text-white rounded-lg resize-none"
          placeholder="Type your message..."
          value={chatInput}
          onChange={(e) => setChatInput(e.target.value)}
          onKeyDown={handleKeyDown}
          rows={3}
        />
        <button
          className="mt-2 bg-yellow text-black rounded p-2 w-24 self-end hover:brightness-90"
          onClick={handleSend}
        >
          Send
        </button>
      </div>
    </div>
  );
};

export default Chat;